export const FIELD_TYPES = {
  TEXT: "text",
  RICHTEXT: "richtext",
  EMAIL: "email",
  MEDIA: "media",
};

const ORDER_VARS = ["{email}", "{invoice_no}"];
const EMAIL_VARS = ["{email}"];
const GLOBAL_VARS = ["{site_title}", "{site_url}", "{current_date}", "{current_datetime}"];

export const EMAIL_TEMPLATE_TABS = [
  {
    id: "order_confirmation",
    label: "Order Confirmation",
    icon: "bi bi-cart-check",
    description: "Messages sent when a new order is placed (COD, cash, or receipt).",
    subsections: [
      {
        id: "order_mail_customer",
        label: "Customer Order Email",
        hint: "Sent to the customer when an order is placed. Product table and totals are fixed in the layout.",
        fields: [
          {
            label: "Customer Message",
            name: "order_mail_customer_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            description: "Message sent to the customer when an order is placed",
            variables: [...EMAIL_VARS, ...ORDER_VARS.slice(1), ...GLOBAL_VARS],
          },
        ],
      },
      {
        id: "order_mail_admin",
        label: "Admin New Order Email",
        hint: "Sent to admin when a new order is placed.",
        fields: [
          {
            label: "Admin Message",
            name: "order_mail_admin_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            description: "Message sent to admin when a new order is placed",
            variables: [...EMAIL_VARS, "{current_datetime}", "{site_title}"],
          },
        ],
      },
    ],
  },
  {
    id: "payment",
    label: "Payment Emails",
    icon: "bi bi-credit-card",
    description: "Emails sent after successful or failed payment.",
    subsections: [
      {
        id: "payment_completed",
        label: "Payment Completed",
        fields: [
          {
            label: "Main Title",
            name: "payment_completed_title",
            type: FIELD_TYPES.TEXT,
            required: true,
            variables: GLOBAL_VARS,
          },
          {
            label: "Payment Received Title",
            name: "payment_completed_received_title",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "Payment Received Message",
            name: "payment_completed_received_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
          },
          {
            label: "Greeting",
            name: "payment_completed_greeting",
            type: FIELD_TYPES.TEXT,
            required: true,
            variables: EMAIL_VARS,
          },
          {
            label: "Main Message",
            name: "payment_completed_main_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: [...EMAIL_VARS, "{invoice_no}", "{current_date}"],
          },
          {
            label: "Preparation Message",
            name: "payment_completed_preparation_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
          },
          {
            label: "Support Message",
            name: "payment_completed_support_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: ["{site_url}", "{site_title}"],
          },
        ],
      },
      {
        id: "payment_failed",
        label: "Payment Failed",
        hint: "Gateway rejection reason is shown from payment data, not from these templates.",
        fields: [
          {
            label: "Main Title",
            name: "payment_failed_title",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "Alert Heading",
            name: "payment_failed_heading",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "Alert Message",
            name: "payment_failed_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
          },
          {
            label: "Greeting",
            name: "payment_failed_greeting",
            type: FIELD_TYPES.TEXT,
            required: true,
            variables: EMAIL_VARS,
          },
          {
            label: "Main Message",
            name: "payment_failed_main_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: [...EMAIL_VARS, "{invoice_no}"],
          },
          {
            label: "Retry Message",
            name: "payment_failed_retry_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: ["{site_url}"],
          },
        ],
      },
    ],
  },
  {
    id: "order_status",
    label: "Order Status Emails",
    icon: "bi bi-truck",
    description: "Status updates, processing, shipping, and delivery notifications.",
    subsections: [
      {
        id: "order_status_updated",
        label: "Status Update",
        hint: "May be sent together with a dedicated processing/shipped/delivered email.",
        fields: [
          {
            label: "Main Title",
            name: "order_status_updated_title",
            type: FIELD_TYPES.TEXT,
            required: true,
            variables: ["{site_title}"],
          },
          {
            label: "Greeting",
            name: "order_status_updated_greeting",
            type: FIELD_TYPES.TEXT,
            required: true,
            variables: EMAIL_VARS,
          },
          {
            label: "Update Message",
            name: "order_status_updated_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: ["{current_date}"],
          },
          {
            label: "Processing Message",
            name: "order_status_processing_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
          },
          {
            label: "Shipped Message",
            name: "order_status_shipped_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
          },
          {
            label: "Delivered Message",
            name: "order_status_delivered_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: ["{site_title}"],
          },
          {
            label: "Support Message",
            name: "order_status_support_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: ["{site_url}"],
          },
        ],
      },
      {
        id: "order_processing",
        label: "Order Processing",
        fields: [
          {
            label: "Main Title",
            name: "order_processing_title",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "Working Title",
            name: "order_processing_working_title",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "Working Message",
            name: "order_processing_working_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
          },
          {
            label: "Greeting",
            name: "order_processing_greeting",
            type: FIELD_TYPES.TEXT,
            required: true,
            variables: EMAIL_VARS,
          },
          {
            label: "Main Message",
            name: "order_processing_main_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: [...EMAIL_VARS, "{invoice_no}"],
          },
          {
            label: "Delivery Title",
            name: "order_processing_delivery_title",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "Delivery Message",
            name: "order_processing_delivery_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
          },
          {
            label: "Expected Date Label",
            name: "order_processing_expected_date",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "Support Message",
            name: "order_processing_support_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: ["{site_url}"],
          },
        ],
      },
      {
        id: "order_shipped",
        label: "Order Shipped",
        fields: [
          {
            label: "Main Title",
            name: "order_shipped_title",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "On The Way Title",
            name: "order_shipped_way_title",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "On The Way Message",
            name: "order_shipped_way_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
          },
          {
            label: "Greeting",
            name: "order_shipped_greeting",
            type: FIELD_TYPES.TEXT,
            required: true,
            variables: EMAIL_VARS,
          },
          {
            label: "Main Message",
            name: "order_shipped_main_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: [...EMAIL_VARS, "{invoice_no}", "{current_date}"],
          },
          {
            label: "Tracking Message",
            name: "order_shipped_tracking_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: ["{site_url}", "{site_title}"],
          },
        ],
      },
      {
        id: "order_delivered",
        label: "Order Delivered",
        fields: [
          {
            label: "Main Title",
            name: "order_delivered_title",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "Success Title",
            name: "order_delivered_success_title",
            type: FIELD_TYPES.TEXT,
            required: true,
          },
          {
            label: "Success Message",
            name: "order_delivered_success_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
          },
          {
            label: "Greeting",
            name: "order_delivered_greeting",
            type: FIELD_TYPES.TEXT,
            required: true,
            variables: EMAIL_VARS,
          },
          {
            label: "Main Message",
            name: "order_delivered_main_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: [...EMAIL_VARS, "{invoice_no}", "{current_date}"],
          },
          {
            label: "Thank You Message",
            name: "order_delivered_thank_you_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: ["{site_title}", "{site_url}"],
          },
          {
            label: "Support Message",
            name: "order_delivered_support_message",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            variables: ["{site_url}"],
          },
        ],
      },
    ],
  },
  {
    id: "appearance",
    label: "Email Appearance",
    icon: "bi bi-palette",
    description: "Global settings that affect all email templates.",
    subsections: [
      {
        id: "appearance_global",
        label: "Global Email Settings",
        fields: [
          {
            label: "Site Title",
            name: "site_title",
            type: FIELD_TYPES.TEXT,
            required: true,
            description: "Used as {site_title} in all templates",
          },
          {
            label: "Global From Email",
            name: "site_global_email",
            type: FIELD_TYPES.EMAIL,
            required: false,
            description: "Also updates MAIL_FROM_ADDRESS when saved",
          },
          {
            label: "Email Header",
            name: "basic_mail_header",
            type: FIELD_TYPES.TEXT,
            required: true,
            variables: ["{site_title}"],
          },
          {
            label: "Footer Copyright",
            name: "site_footer_copyright",
            type: FIELD_TYPES.RICHTEXT,
            required: true,
            description: "Footer shown in all email templates — supports HTML",
            variables: GLOBAL_VARS,
          },
          {
            label: "Invoice Logo",
            name: "invoice_logo",
            type: FIELD_TYPES.MEDIA,
            required: false,
            description: "Logo displayed at the top of order emails",
          },
        ],
      },
    ],
  },
];

export function getAllFieldNames() {
  return EMAIL_TEMPLATE_TABS.flatMap((tab) =>
    tab.subsections.flatMap((section) => section.fields.map((field) => field.name))
  );
}

export function getTabFieldNames(tabId) {
  const tab = EMAIL_TEMPLATE_TABS.find((item) => item.id === tabId);
  if (!tab) return [];
  return tab.subsections.flatMap((section) => section.fields.map((field) => field.name));
}

export function getFieldByName(fieldName) {
  for (const tab of EMAIL_TEMPLATE_TABS) {
    for (const section of tab.subsections) {
      const field = section.fields.find((item) => item.name === fieldName);
      if (field) return field;
    }
  }
  return null;
}

/** @deprecated Use EMAIL_TEMPLATE_TABS — kept for backward compatibility */
export const emailTemplateFields = EMAIL_TEMPLATE_TABS.map((tab) => ({
  category: tab.label,
  fields: tab.subsections.flatMap((section) => section.fields),
}));
